if (!customElements.get('checkbox-cart')) {
    customElements.define(
      'checkbox-cart',
      class CheckboxCart extends HTMLElement {
        constructor() {
          super();
          this.checkboxes = this.querySelectorAll('.checkbox-container .checkbox-input');
          this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          this.cartItems = this.cart ? (this.cart.querySelector('cart-items') || this.cart.querySelector('cart-drawer-items')) : null;
          
          // Track variants being removed to prevent checkbox blinking
          this.removingVariants = new Set();
          // Track all variants being processed
          this.processingVariants = new Set();
          
          // Setup event listeners for checkboxes
          this.checkboxes.forEach(checkbox => {
            checkbox.addEventListener('change', this.onCheckboxChangeHandler.bind(this));
          });
          
          this.fetchCartAndSetupCheckboxes();
          this.setupCartChangeListeners();
        }

        onCheckboxChangeHandler(evt) {
          const checkbox = evt.target;
          const variantId = checkbox.getAttribute('data-variant-id');
          
          if (!variantId) return;
          
          // Skip if already processing this variant
          if (this.processingVariants.has(variantId)) {
            evt.preventDefault();
            return;
          }
          
          // Mark as processing
          this.processingVariants.add(variantId);
          
          // Disable the checkbox during processing
          checkbox.setAttribute('aria-disabled', true);
          checkbox.disabled = true;
                
          if (checkbox.checked) {
            this.addToCart(variantId, checkbox);
          } else {
            // Track that we're removing this variant to prevent blinking
            this.removingVariants.add(variantId);
            this.removeFromCart(variantId, checkbox);
          }
        }

        addToCart(variantId, checkbox) {
          // Create fetch configuration
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          
          // Prepare form data
          const formData = new FormData();
          formData.append('id', variantId);
          formData.append('quantity', 1);
          
          // Add sections for cart drawer rendering
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            if (typeof this.cart.setActiveElement === 'function') {
              this.cart.setActiveElement(document.activeElement);
            }
          }
          
          config.body = formData;
          
          // Add item to cart (without checking if it already exists)
          fetch(`${routes.cart_add_url}`, config)
            .then(response => response.json())
            .then(response => {
              if (response.status) {
                // Handle error
                if (typeof publish === 'function') {
                  publish(PUB_SUB_EVENTS.cartError, {
                    source: 'checkbox-cart',
                    productVariantId: variantId,
                    errors: response.errors || response.description,
                    message: response.message,
                  });
                }
                this.error = true;
                return;
              } else if (!this.cart) {
                window.location = window.routes.cart_url;
                return;
              }
        
              // Success - publish event
              this.error = false;
              if (typeof publish === 'function') {
                publish(PUB_SUB_EVENTS.cartUpdate, { 
                  source: 'checkbox-cart', 
                  productVariantId: variantId 
                });
              }
              
              // Update cart contents without opening drawer
              if (this.cart) {
                this.cart.renderContents(response, false);
              }
            })
            .catch(e => {
            //   console.error('Error adding to cart:', e);
            })
            .finally(() => {
              this.finishLoading(checkbox, variantId);
            });
        }

        removeFromCart(variantId, checkbox) {
          if (this.cartItems && typeof this.cartItems.updateQuantity === 'function') {
            // Find the line item index for this variant ID
            this.findLineItemIndex(variantId)
              .then(lineItemIndex => {
                if (lineItemIndex !== -1) {

                  this.cartItems.updateQuantity(lineItemIndex, 0, event);
                  
                  if (typeof publish === 'function') {
                    publish(PUB_SUB_EVENTS.cartUpdate, { 
                      source: 'checkbox-cart', 
                      productVariantId: variantId 
                    });
                  }
                  
                  // Finish loading state after a slight delay to allow cart update to complete
                  setTimeout(() => {
                    this.finishLoading(checkbox, variantId);
                    
                    // Remove variant from tracking after a longer delay
                    // to ensure cart is fully updated
                    setTimeout(() => {
                      this.removingVariants.delete(variantId);
                    }, 1000);
                  }, 300);
                } else {
                  this.finishLoading(checkbox, variantId);
                  this.removingVariants.delete(variantId);
                }
              })
              .catch(error => {
                this.finishLoading(checkbox, variantId);
                this.removingVariants.delete(variantId);
              });
          } else {
            this.finishLoading(checkbox, variantId);
            this.removingVariants.delete(variantId);
          }
        }
        
        // Helper method to find the line item index for a variant ID
        findLineItemIndex(variantId) {
          return new Promise((resolve, reject) => {
            const variantIdString = variantId.toString();
            
            const cartDrawerItems = document.querySelector('cart-drawer-items');
            
            if (!cartDrawerItems) {
              reject(new Error('Could not find cart-drawer-items container'));
              return;
            }
            
            // Look for quantity inputs with matching variant ID within the cart drawer
            const quantityInput = cartDrawerItems.querySelector(
              `[data-quantity-variant-id="${variantIdString}"]`
            );
            
            if (quantityInput) {
              const index = parseInt(quantityInput.getAttribute('data-index'), 10);
              resolve(index);
            } else {
              reject(new Error(`Could not find quantity input for variant ${variantIdString}`));
            }
          });
        }

        finishLoading(checkbox, variantId) {       
          // Get latest cart data first before re-enabling the checkbox
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              if (this.cart) {
                // Properly update empty state
                if (cart.item_count === 0 && !this.cart.classList.contains('is-empty')) {
                  this.cart.classList.add('is-empty');
                } else if (cart.item_count > 0 && this.cart.classList.contains('is-empty')) {
                  this.cart.classList.remove('is-empty');
                }
              }
              
              // Update checkbox states
              this.updateCheckboxStates(cart);
              
              // Only now re-enable the checkbox
              setTimeout(() => {
                checkbox.disabled = false;
                checkbox.removeAttribute('aria-disabled');
                this.processingVariants.delete(variantId);
              }, 200);
            })
            .catch(e => {
              // console.error('Error fetching cart:', e);
              // Still re-enable on error after a delay
              setTimeout(() => {
                checkbox.disabled = false;
                checkbox.removeAttribute('aria-disabled');
                this.processingVariants.delete(variantId);
              }, 200);
            });
        }

        fetchCartAndSetupCheckboxes() {
          fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
              // Update cart empty state if needed
              if (this.cart) {
                if (cart.item_count === 0 && !this.cart.classList.contains('is-empty')) {
                  this.cart.classList.add('is-empty');
                } else if (cart.item_count > 0 && this.cart.classList.contains('is-empty')) {
                  this.cart.classList.remove('is-empty');
                }
              }
              
              // Update checkbox states
              this.updateCheckboxStates(cart);
            })
            .catch(error => {
                // console.error('Error fetching cart:', error);
            })
        }

        updateCheckboxStates(cart) {
          // Create a Set of variant IDs in cart for faster lookups
          const cartVariantIds = new Set();
          
          // Also track quantities to ensure we're not showing checked for 0 quantity items
          const variantQuantities = {};
          
          if (cart?.items) {
            cart.items.forEach(item => {
              if (item.quantity > 0) {
                cartVariantIds.add(item.variant_id.toString());
                variantQuantities[item.variant_id.toString()] = item.quantity;
              }
            });
          }
          
          // Update all checkbox states
          document.querySelectorAll('.checkbox-input').forEach(checkbox => {
            const variantId = checkbox.getAttribute('data-variant-id');
            if (!variantId) return;
            
            // Skip if checkbox is currently being interacted with or processing
            if (checkbox.disabled || this.processingVariants.has(variantId)) return;
            
            // IMPORTANT FIX: Skip variants that are being removed to prevent blinking
            if (this.removingVariants.has(variantId)) {
              // Force checkbox to unchecked state for variants being removed
              checkbox.checked = false;
              return;
            }
            
            // Set checkbox state (checked if in cart, unchecked if not)
            checkbox.checked = cartVariantIds.has(variantId);
          });
        }

        setupCartChangeListeners() {
          // Subscribe to cart update events
          if (typeof subscribe === 'function') {
            this.cartUpdateUnsubscriber = subscribe(
              PUB_SUB_EVENTS.cartUpdate,
              this.handleCartUpdate.bind(this)
            );
          }
          
          // Listen for cart drawer changes via mutation observer
          if (window.MutationObserver && this.cartItems) {
            const cartObserver = new MutationObserver((mutations) => {
              for (const mutation of mutations) {
                if (mutation.type === 'childList' || 
                    (mutation.type === 'attributes' && mutation.attributeName === 'class')) {
                  this.fetchCartAndSetupCheckboxes();
                  break;
                }
              }
            });
            
            cartObserver.observe(this.cartItems, { 
              childList: true, 
              subtree: true,
              attributes: true,
              attributeFilter: ['class']
            });
          }
        }

        handleCartUpdate(event) {
          // Only update if the event isn't from our own component
          if (event?.source !== 'checkbox-cart') {
            this.fetchCartAndSetupCheckboxes();
          }
        }
      }
    );
  }